<template>
  <div class="account-selector-container">
    <!-- Current account display -->
    <div class="current-account" v-bind="menuProps">
      <div class="d-flex align-center">
        <v-avatar size="32" class="account-initials-avatar mr-3" color="primary">
          <v-img v-if="selectedAccount.logoURL" :src="selectedAccount.logoURL" alt="logo" />
          <div v-else>
            {{ getInitials(selectedAccount.name) }}
          </div>
        </v-avatar>
        <div>
          <div class="account-name">{{ selectedAccount.name }}</div>
          <div class="current-label">{{ $t("accounts.currentAccount") }}</div>
        </div>
      </div>
      <v-icon :icon="isMenuOpen ? 'mdi-chevron-up' : 'mdi-chevron-down'" />
    </div>

    <!-- Menu for account selection -->
    <v-menu v-model="isMenuOpen" :close-on-content-click="false" location="bottom" offset-y>
      <template v-slot:activator="{ props }">
        <div class="d-none" v-bind="props"></div>
      </template>

      <v-card class="account-menu-card" min-width="240px">
        <!-- Available accounts section -->
        <div v-if="accounts.length > 0" class="accounts-section">
          <div class="section-header">
            <span>{{ $t("accounts.otherAccounts") }}</span>
            <div class="accounts-counter">{{ accounts.length }}</div>
          </div>

          <!-- Top shadow indicator -->
          <div class="top-shadow" :class="{ visible: showTopShadow }"></div>

          <div class="accounts-list" ref="accountsList" @scroll="checkScrollShadows">
            <div
              v-for="account in accounts"
              :key="account.id"
              class="account-item"
              @click="selectAccount(account)"
            >
              <v-avatar size="28" class="account-initials-avatar mr-2" color="primary">
                <v-img v-if="account.logoURL" :src="account.logoURL" alt="logo" />
                <div v-else>
                  {{ getInitials(account.name) }}
                </div>
              </v-avatar>
              <div class="account-name dense">{{ account.name }}</div>
            </div>
          </div>

          <!-- Bottom shadow indicator -->
          <div class="bottom-shadow" :class="{ visible: showBottomShadow }"></div>
        </div>

        <!-- Add new account button with divider -->
        <div class="new-account-section">
          <div class="divider"></div>
          <v-btn
            variant="text"
            block
            class="text-none"
            prepend-icon="mdi-plus-circle"
            @click="onNewAccount"
          >
            {{ $t("accounts.newAccount") }}
          </v-btn>
        </div>
      </v-card>
    </v-menu>
  </div>
  <add-account-dialog ref="accountDialog" />
</template>

<script lang="ts" setup>
import { useAuthStore } from "@/auth/stores/auth";
import { useAccountStore } from "@/account/stores/account";
import { computed, ref, nextTick, watch } from "vue";
import AddAccountDialog from "@/account/components/account/AddAccountDialog.vue";
import { getInitials } from "@/shared/utils/initials.utils";

interface Account {
  name: string;
  id: string;
  logoURL: string;
}

const selectedAccount = ref<Account>({ name: "", id: "", logoURL: "" });
const accountDialog = ref<null | { open: () => null }>(null);
const isMenuOpen = ref(false);
const authStore = useAuthStore();
const accountStore = useAccountStore();
const accountsList = ref<HTMLElement | null>(null);
const isScrollable = ref(false);
const showTopShadow = ref(false);
const showBottomShadow = ref(false);

const emit = defineEmits(["updateInitials"]);

const userAccounts = computed(() => authStore.getUserAccounts);

// This will now include all accounts except the selected one
const accounts = computed(() => {
  return Object.entries(userAccounts.value)
    .map(([key, val]) => ({
      name: val.name,
      id: key,
      logoURL: val.logoURL,
    }))
    .filter((account) => account.id !== selectedAccount.value.id);
});

// Props to toggle the menu when clicking on the current account
const menuProps = computed(() => ({
  onClick: () => {
    isMenuOpen.value = !isMenuOpen.value;
  },
}));

const selectAccount = (account: Account) => {
  selectedAccount.value = account;
  onChangeSession();
  isMenuOpen.value = false;
};

const onChangeSession = () => {
  useAuthStore().changeSession({ accountId: selectedAccount.value.id });
};

const onNewAccount = () => {
  accountDialog.value?.open();
  isMenuOpen.value = false;
};

// Check if the accounts list is scrollable and update shadow visibility
const checkScrollShadows = () => {
  if (accountsList.value) {
    const { scrollTop, scrollHeight, clientHeight } = accountsList.value;

    // Check if scrollable
    isScrollable.value = scrollHeight > clientHeight;

    // Show top shadow if not at the top
    showTopShadow.value = scrollTop > 0;

    // Show bottom shadow if not at the bottom
    showBottomShadow.value = scrollTop + clientHeight < scrollHeight;
  }
};

// Watch for menu open state to check scrollability after DOM update
watch(isMenuOpen, (isOpen) => {
  if (isOpen) {
    nextTick(() => {
      checkScrollShadows();
    });
  }
});

// Watch for accounts changes to update scrollability
watch(
  () => accounts.value.length,
  () => {
    if (isMenuOpen.value) {
      nextTick(() => {
        checkScrollShadows();
      });
    }
  },
);

watch(
  () => [
    accountStore.account?.id,
    accountStore.getAccountDisplayName,
    accountStore.account?.logoURL,
  ],
  () => {
    selectedAccount.value = {
      name: accountStore.getAccountDisplayName,
      id: accountStore.account?.id || "",
      logoURL: accountStore.account?.logoURL || "",
    };
    emit("updateInitials", getInitials(selectedAccount.value.name));
  },
  { immediate: true },
);
</script>

<style scoped>
.account-selector-container {
  position: relative;
  width: 100%;
}

.account-initials-avatar {
  color: #ffffff !important;
  font-weight: 700;
  box-shadow: none !important;
}

.current-account {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 14px;
  border-radius: 8px;
  background-color: rgb(var(--v-theme-surface));
  border: 1px solid rgba(var(--v-theme-on-surface), 0.12);
  cursor: pointer;
  transition: all 0.2s ease;
}

.current-account:hover {
  background-color: rgba(var(--v-theme-on-surface), 0.04);
}

.account-name {
  font-weight: 500;
  font-size: 0.95rem;
  color: rgb(var(--v-theme-on-surface));
}

.account-name.dense {
  font-size: 0.9rem;
}

.current-label {
  font-size: 0.75rem;
  color: rgba(var(--v-theme-on-surface), 0.7);
  margin-top: 1px;
}

.account-menu-card {
  border-radius: 8px;
  overflow: hidden;
  width: 100%;
  max-width: 280px;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  font-weight: 500;
  color: rgba(var(--v-theme-on-surface), 0.7);
  font-size: 0.85rem;
  background-color: rgba(var(--v-theme-on-surface), 0.04);
}

.accounts-counter {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 22px;
  height: 22px;
  border-radius: 11px;
  background-color: rgba(var(--v-theme-primary), 0.1);
  color: rgb(var(--v-theme-primary));
  font-size: 0.75rem;
  font-weight: 700;
  padding: 0 6px;
}

.accounts-section {
  position: relative;
}

.accounts-list {
  max-height: 220px;
  overflow-y: auto;
  position: relative;
  z-index: 1;
}

.account-item {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.account-item:hover {
  background-color: rgba(var(--v-theme-primary), 0.05);
}

.top-shadow,
.bottom-shadow {
  position: absolute;
  left: 0;
  right: 0;
  height: 12px;
  z-index: 2;
  opacity: 0;
  transition: opacity 0.2s ease;
  pointer-events: none;
}

.top-shadow {
  top: 35px; /* Position below header */
  background: linear-gradient(to bottom, rgba(0, 0, 0, 0.15), transparent);
}

.bottom-shadow {
  bottom: 0;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.15), transparent);
}

.top-shadow.visible,
.bottom-shadow.visible {
  opacity: 1;
}

.new-account-section {
  padding: 8px 12px;
  position: relative;
}

.divider {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 1px;
  background-color: rgba(var(--v-theme-on-surface), 0.12);
}
</style>
